import { Button } from 'components';
import LinkText from 'components/Text/LinkText';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { INTERNAL_LINKS, STATUS_LIST } from '../../../enum';
import useInfinitePeriodByGroupQuery from '../../../hooks/queries/useInfinitePeriodByGroupQuery';
import { cleanQueryParams } from '../../../utils/queryParams';
import { selectStoreCompanySettings } from '../../../utils/storeSelectors';
import { formatPaymentPeriodLabel } from '../../../utils/trip-receipts';
import classNames from './styles.module.scss';
import {
  getActiveUrgency,
  getDaysUntil,
  sameCalendarDay,
  selectionMatchesPresentPeriod,
  urgencyPillText,
} from './utils';

const usePipelineModel = ({ timeline, timelineFilters, isTimelineLoading, t }) => {
  const companySettings = useSelector(selectStoreCompanySettings);

  const { query: groupPeriodsQuery, flatPages: groupPeriods = [] } = useInfinitePeriodByGroupQuery(
    timelineFilters?.groupID,
    {
      enabled: !!timelineFilters?.groupID,
    },
  );

  const submissions = useMemo(() => timeline?.find(e => e.type === 'submissions'), [timeline]);
  const approvals = useMemo(() => timeline?.find(e => e.type === 'approvals'), [timeline]);
  const payments = useMemo(() => timeline?.find(e => e.type === 'payments'), [timeline]);

  /**
   * "Current" for the pipeline must match the period dropdown: either today falls in the range,
   * or the selected range is the API's `present` period (labeled "(current)"). Using only
   * isBetween caused mismatches when boundaries/timezones didn't include "today" while the
   * dropdown still showed that row as the current period.
   */
  const isCurrentPeriod = useMemo(() => {
    if (!timelineFilters?.startDate || !timelineFilters?.endDate) return null;
    // Stay null (unknown) until groupPeriods has loaded — avoids a flash of "past period"
    // on the first render when groupPeriods is still an empty array.
    if (timelineFilters?.groupID && !groupPeriodsQuery.isFetched) return null;
    const start = moment(timelineFilters.startDate);
    const end = moment(timelineFilters.endDate);
    const todayInsideRange = moment().isBetween(start, end, 'day', '[]');
    const isPresentSelection = selectionMatchesPresentPeriod(
      timelineFilters.startDate,
      timelineFilters.endDate,
      groupPeriods,
    );
    return todayInsideRange || isPresentSelection;
  }, [groupPeriods, groupPeriodsQuery.isFetched, timelineFilters]);

  const pastPeriodRangeLabel = useMemo(() => {
    if (!timelineFilters?.startDate || !timelineFilters?.endDate) return '';
    const matched = groupPeriods.find(
      p =>
        sameCalendarDay(p.start, timelineFilters.startDate) &&
        sameCalendarDay(p.end, timelineFilters.endDate),
    );
    if (matched) return formatPaymentPeriodLabel(matched);
    return `${moment(timelineFilters.startDate).format('MM/DD')} - ${moment(
      timelineFilters.endDate,
    ).format('MM/DD')}`;
  }, [groupPeriods, timelineFilters]);

  const hasPeriod = !!(timelineFilters?.startDate && timelineFilters?.endDate);

  const isFuturePeriod = useMemo(() => {
    if (!timelineFilters?.startDate) return false;
    return moment(timelineFilters.startDate).isAfter(moment(), 'day');
  }, [timelineFilters?.startDate]);

  const currentPeriodDates = useMemo(() => {
    const present = groupPeriods?.find(p => p.period === 'present');
    if (!present?.start || !present?.end) return null;
    return { startDate: present.start, endDate: present.end };
  }, [groupPeriods]);

  const stage2Complete =
    approvals && approvals.sumOfReceipts > 0 && approvals.approved >= approvals.sumOfReceipts;

  const pipelineModel = useMemo(() => {
    if (!hasPeriod || isTimelineLoading || !timeline?.length) return null;

    const approveLinkRoot = companySettings?.hideReceipts
      ? INTERNAL_LINKS.REIMBURSEMENT
      : INTERNAL_LINKS.RECEIPTS;
    const approveLink = {
      pathname: approveLinkRoot,
      search: `?${cleanQueryParams({
        groupId: timelineFilters?.groupID || undefined,
        status: STATUS_LIST().Status.PENDING,
      })}`,
    };

    const submissionsMetaR = submissions ? (
      <>
        <strong>{submissions.submitted}</strong> / {submissions.sumOfUsers} {t('pipelineEmployees')}
      </>
    ) : (
      '—'
    );

    // ── Historical (past period) ─────────────────────────────────────────
    if (isCurrentPeriod === false) {
      const subPct =
        submissions && submissions.sumOfUsers > 0
          ? Math.min(100, (100 * submissions.submitted) / submissions.sumOfUsers)
          : 100;
      const apprPct =
        approvals && approvals.sumOfReceipts > 0
          ? Math.min(100, (100 * approvals.approved) / approvals.sumOfReceipts)
          : 100;

      return {
        periodPill: `${pastPeriodRangeLabel} · ${t('pastPeriod')}`,
        s1: {
          cardModifier: 'sFuture',
          pillModifier: 'pillFuture',
          pillText: '✓\u00A0Submitted',
          fill: 'fillGrey',
          w: subPct,
          metaL: t('submitted'),
          metaR: submissionsMetaR,
          footer: null,
        },
        s2: {
          cardModifier: 'sFuture',
          pillModifier: 'pillFuture',
          pillText: '✓\u00A0Approved',
          fill: 'fillGrey',
          w: apprPct,
          metaL: t('approved'),
          metaR: approvals ? (
            <>
              <strong>{approvals.approved}</strong> / {approvals.sumOfReceipts}{' '}
              {t('pipelineApprovedLower')}
            </>
          ) : (
            '—'
          ),
          footer: (
            <Link to={approveLink} className={classNames.ctaLink}>
              <LinkText>{t('viewApprovals')}</LinkText>
            </Link>
          ),
        },
        s3: {
          cardModifier: 'sFuture',
          pillModifier: 'pillFuture',
          pillText: '✓\u00A0Completed',
          fill: null,
          w: null,
          metaL: null,
          metaR: null,
          footer: null,
        },
      };
    }

    // ── Current period ───────────────────────────────────────────────────
    const daysSub = getDaysUntil(submissions?.date);
    const submissionPassed = daysSub !== null && daysSub < 0;
    const allDriversSubmitted =
      submissions && submissions.sumOfUsers > 0 && submissions.submitted >= submissions.sumOfUsers;

    const subW =
      submissions && submissions.sumOfUsers > 0
        ? Math.min(100, (100 * submissions.submitted) / submissions.sumOfUsers)
        : 0;

    let s1 = {};
    if (submissionPassed) {
      s1 = {
        cardModifier: 'sComplete',
        pillModifier: 'pillComplete',
        pillText: allDriversSubmitted
          ? '✓\u00A0All Submitted'
          : `✓\u00A0${submissions?.submitted ?? 0} Submitted`,
        fill: 'fillGreen',
        w: submissions && submissions.sumOfUsers > 0 ? subW : 100,
        metaL: t('submitted'),
        metaR: submissionsMetaR,
        footer: null,
      };
    } else if (daysSub !== null && daysSub > 7) {
      s1 = {
        cardModifier: 'sFuture',
        pillModifier: 'pillFuture',
        pillText: `🕐\u00A0${daysSub} Days Away`,
        fill: 'fillGrey',
        w: subW,
        metaL: t('submitted'),
        metaR: submissionsMetaR,
        footer: null,
      };
    } else if (daysSub === null) {
      s1 = {
        cardModifier: 'sFuture',
        pillModifier: 'pillFuture',
        pillText: '🕐\u00A0—',
        fill: 'fillGrey',
        w: 0,
        metaL: t('submitted'),
        metaR: '—',
        footer: null,
      };
    } else {
      const u = getActiveUrgency(submissions?.date);
      s1 = {
        cardModifier: u.card,
        pillModifier: u.pill,
        pillText: urgencyPillText(u, daysSub),
        fill: u.fill,
        w: subW,
        metaL: t('submitted'),
        metaR: submissionsMetaR,
        footer: null,
      };
    }

    let s2 = {};
    if (stage2Complete) {
      s2 = {
        cardModifier: 'sComplete',
        pillModifier: 'pillComplete',
        pillText: '✓\u00A0All Approved',
        fill: 'fillGreen',
        w: 100,
        metaL: t('approved'),
        metaR: approvals ? (
          <>
            <strong>{approvals.approved}</strong> / {approvals.sumOfReceipts}{' '}
            {t('approved').toLowerCase()}
          </>
        ) : (
          '—'
        ),
        footer: (
          <Link to={approveLink} style={{ display: 'block', width: '100%' }}>
            <Button block type="primary">
              {t('approveReimbursements')}
            </Button>
          </Link>
        ),
      };
    } else {
      const u = getActiveUrgency(approvals?.date);
      const d = getDaysUntil(approvals?.date);
      const apprW =
        approvals && approvals.sumOfReceipts > 0
          ? Math.min(100, (100 * approvals.approved) / approvals.sumOfReceipts)
          : 0;
      const ctaType = u.key === 'urgent' || u.key === 'warning' ? 'amber' : 'teal';
      const pendingCount = Math.max(
        0,
        (approvals?.sumOfReceipts ?? 0) - (approvals?.approved ?? 0),
      );
      const metaApprovedRight =
        pendingCount > 0 ? (
          <>
            <strong>{approvals?.approved ?? 0}</strong> / {pendingCount} {t('pipelinePending')}
          </>
        ) : (
          <>
            <strong>{approvals?.approved ?? 0}</strong> / {approvals?.sumOfReceipts ?? 0}{' '}
            {t('pipelineApprovedLower')}
          </>
        );

      s2 = {
        cardModifier: u.card,
        pillModifier: u.pill,
        pillText: urgencyPillText(u, d),
        fill: u.fill,
        w: apprW,
        metaL: t('approved'),
        metaR: metaApprovedRight,
        footer: (
          <Link to={approveLink} style={{ width: '100%' }}>
            <Button block type={ctaType}>
              {t('approveReimbursements')}
            </Button>
          </Link>
        ),
      };
    }

    let s3 = {};
    if (!stage2Complete) {
      const d = getDaysUntil(payments?.date);
      const pillText =
        d !== null && d > 0
          ? `🕐\u00A0${d} Days Away`
          : d === 0
          ? '🕐\u00A0Due Today'
          : '🕐\u00A0—';
      s3 = {
        cardModifier: 'sFuture',
        pillModifier: 'pillFuture',
        pillText,
        fill: null,
        w: null,
        metaL: null,
        metaR: null,
        footer: (
          <Button block type="ghost" disabled>
            {t('awaitingApprovals')}
          </Button>
        ),
      };
    } else {
      const d = getDaysUntil(payments?.date);
      if (d === null) {
        s3 = {
          cardModifier: 'sFuture',
          pillModifier: 'pillFuture',
          pillText: '🕐\u00A0—',
          fill: null,
          w: null,
          metaL: null,
          metaR: null,
          footer: null,
        };
      } else {
        const u = getActiveUrgency(payments?.date);
        const prefix = u.key === 'safe' ? '🟢\u00A0' : u.key === 'future' ? '🕐\u00A0' : '🟢\u00A0';
        const pillLabel =
          u.key === 'future'
            ? `${d} Days Away`
            : u.key === 'urgent' && d === 0
            ? 'Due Today'
            : u.key === 'urgent'
            ? `${Math.abs(d)} Days Overdue`
            : u.key === 'warning'
            ? `${d} Days Left`
            : `${d} Days Away`;

        s3 = {
          cardModifier: u.card === 'sFuture' ? 'sSafe' : u.card,
          pillModifier: u.key === 'future' ? 'pillFuture' : u.pill,
          pillText: `${prefix}${pillLabel}`,
          fill: null,
          w: null,
          metaL: null,
          metaR: null,
          footer: null,
        };
      }
    }

    return { periodPill: t('currentPeriod'), s1, s2, s3 };
  }, [
    approvals,
    hasPeriod,
    isCurrentPeriod,
    isTimelineLoading,
    pastPeriodRangeLabel,
    stage2Complete,
    submissions,
    payments,
    t,
    timeline,
    timelineFilters?.groupID,
  ]);

  return {
    pipelineModel,
    isCurrentPeriod,
    isFuturePeriod,
    currentPeriodDates,
    hasPeriod,
    submissions,
    approvals,
    payments,
  };
};

export default usePipelineModel;
